import React from "react";

/**
 * Dual-range slider with a colorful gradient rail. Both thumbs are absolutely
 * positioned range inputs so users can drag either bound.
 *
 * The rail maps values to a red → orange → amber → emerald gradient so users
 * get an at-a-glance sense of "low / mid / high" without reading numbers.
 * Used by the /shop filter panel for both price and SibaScore ranges.
 */
export const GradientRangeSlider: React.FC<{
  min: number;
  max: number;
  step: number;
  value: [number, number];
  onChange: (v: [number, number]) => void;
  formatValue: (v: number) => string;
  testIdPrefix: string;
  label: string;
}> = ({ min, max, step, value, onChange, formatValue, testIdPrefix, label }) => {
  const [lo, hi] = value;
  const pctLo = ((lo - min) / (max - min)) * 100;
  const pctHi = ((hi - min) / (max - min)) * 100;
  return (
    <div data-testid={testIdPrefix}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-display font-bold">{label}</div>
        <div className="text-[10px] text-slate-500 dark:text-slate-400 font-display tabular-nums font-semibold" data-testid={`${testIdPrefix}-value`}>
          {formatValue(lo)} – {formatValue(hi)}
        </div>
      </div>
      <div className="relative h-8">
        {/* base gradient rail */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-2 rounded-full"
          style={{ background: "linear-gradient(90deg, #ef4444 0%, #f97316 45%, #f59e0b 65%, #10b981 100%)", opacity: 0.35 }} />
        {/* selected fill */}
        <div className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full"
          style={{
            left: `${pctLo}%`,
            width: `${Math.max(0, pctHi - pctLo)}%`,
            background: "linear-gradient(90deg, #ef4444 0%, #f97316 45%, #f59e0b 65%, #10b981 100%)",
            backgroundSize: `${100 / Math.max(1, (pctHi - pctLo) / 100)}% 100%`,
            backgroundPosition: `-${pctLo}% 0`,
          }} />
        <input type="range" min={min} max={max} step={step} value={lo}
          onChange={(e) => onChange([Math.min(parseInt(e.target.value), hi - step), hi])}
          data-testid={`${testIdPrefix}-min`}
          className="pointer-events-none absolute inset-0 w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-slate-900 dark:[&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-slate-900 [&::-moz-range-thumb]:pointer-events-auto"
        />
        <input type="range" min={min} max={max} step={step} value={hi}
          onChange={(e) => onChange([lo, Math.max(parseInt(e.target.value), lo + step)])}
          data-testid={`${testIdPrefix}-max`}
          className="pointer-events-none absolute inset-0 w-full appearance-none bg-transparent [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-slate-900 dark:[&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-grab [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-slate-900 [&::-moz-range-thumb]:pointer-events-auto"
        />
      </div>
    </div>
  );
};
